import {
  QueryFieldFilterConstraint,
  QueryLimitConstraint,
  QueryOrderByConstraint,
  QueryStartAtConstraint,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  query,
  setDoc,
  writeBatch,
} from 'firebase/firestore'

import { FirebaseCollection } from '@/constants/firebase-collection'
import { firestore } from '@/services/firebase'
import { getPathFileCompanyStock } from '@/utils/storage'
import { delay } from '@/utils/tricks'

import { deleteFile } from '../file'

interface GetListProps {
  companyId: string
  setStockList: (stockList: Stock[] | undefined) => void
  setLoadingList: React.Dispatch<React.SetStateAction<boolean>>
  whereList?: QueryFieldFilterConstraint[]
  orderByList?: QueryOrderByConstraint[]
  startAfter?: QueryStartAtConstraint[]
  limit?: QueryLimitConstraint[]
  timeDelay?: number
}
export const getList = async ({
  companyId,
  setStockList,
  setLoadingList,
  whereList,
  orderByList,
  startAfter,
  limit,
  timeDelay,
}: GetListProps) => {
  setLoadingList(true)

  if (timeDelay) await delay(timeDelay)

  const collectionRef = collection(
    firestore,
    FirebaseCollection.BASE_COMPANY,
    companyId,
    FirebaseCollection.BASE_STOCK,
  )

  const q = query(
    collectionRef,
    ...(whereList || []),
    ...(orderByList || []),
    ...(startAfter || []),
    ...(limit || []),
  )

  onSnapshot(
    q,
    querySnapshot => {
      const stockList: Stock[] = []
      querySnapshot.forEach(doc => {
        const dataItem = { id: doc.id, ...doc.data() } as Stock
        stockList.push(dataItem)
      })
      setLoadingList(false)
      setStockList(stockList)
    },
    () => {
      setLoadingList(false)
      console.log('stock - getList() - error')
    },
  )
}

interface GetOneProps {
  companyId: string
  stockId: string
}
export const getOne = async ({
  companyId,
  stockId,
}: GetOneProps): Promise<Stock | undefined> => {
  const docRef = doc(
    firestore,
    FirebaseCollection.BASE_COMPANY,
    companyId,
    FirebaseCollection.BASE_STOCK,
    stockId,
  )
  const docSnap = await getDoc(docRef)

  try {
    if (docSnap.exists()) {
      return {
        id: stockId,
        ...docSnap.data(),
      } as Stock
    }
  } catch (error) {
    return undefined
  }

  return undefined
}

interface SaveProps {
  companyId: string
  values: Partial<Stock>
}
export const save = async ({ companyId, values }: SaveProps) =>
  await addDoc(
    collection(
      firestore,
      FirebaseCollection.BASE_COMPANY,
      companyId,
      FirebaseCollection.BASE_STOCK,
    ),
    {
      ...values,
      titleSearch: `${(values.title || '')
        .trimEnd()
        .trimStart()}${new Date().getTime()}`.toUpperCase(),
      descriptionSearch: `${(values.description || '')
        .trimEnd()
        .trimStart()}${new Date().getTime()}`.toUpperCase(),
      createdAt: new Date().getTime(),
      dateCreatedAt: new Date().setHours(0, 0, 0, 0),
      updatedAt: new Date().getTime(),
    },
  ).then(resp => resp.id)

interface UpdateProps extends FullUpdate {
  companyId: string
  stockId: string
  values: Partial<Stock>
}
export const update = async ({
  companyId,
  stockId,
  values,
  isFullUpdate = true,
}: UpdateProps) => {
  await setDoc(
    doc(
      firestore,
      FirebaseCollection.BASE_COMPANY,
      companyId,
      FirebaseCollection.BASE_STOCK,
      stockId,
    ),
    {
      ...values,
      ...(isFullUpdate && {
        titleSearch: `${(values.title || '')
          .trimEnd()
          .trimStart()}${new Date().getTime()}`.toUpperCase(),
        descriptionSearch: `${(values.description || '')
          .trimEnd()
          .trimStart()}${new Date().getTime()}`.toUpperCase(),
      }),
      updatedAt: new Date().getTime(),
    },
    { merge: true },
  )
}

interface RemoveProps {
  companyId: string
  stockId: string
}
export const remove = async ({ companyId, stockId }: RemoveProps) => {
  await deleteDoc(
    doc(
      firestore,
      FirebaseCollection.BASE_COMPANY,
      companyId,
      FirebaseCollection.BASE_STOCK,
      stockId,
    ),
  )
}
interface RemovePhotoProps {
  companyId: string
  stockId: string
  photoName: string
}
export const removePhoto = async ({
  companyId,
  stockId,
  photoName,
}: RemovePhotoProps) => {
  await deleteFile({
    pathFile: `${getPathFileCompanyStock(companyId, stockId)}/${photoName}`,
  })
}

interface UpdateInBatchProps {
  companyId: string
  listStock: StockWithCheck[]
}
export const updateInBatch = async ({
  companyId,
  listStock,
}: UpdateInBatchProps) => {
  const batch = writeBatch(firestore)

  listStock.forEach(item => {
    const docRef = doc(
      firestore,
      FirebaseCollection.BASE_COMPANY,
      companyId,
      FirebaseCollection.BASE_STOCK,
      item.id,
    )
    batch.update(docRef, {
      quantity: item.quantity - item.howManyQuantity,
    })
  })

  await batch.commit()
}
